import {
  nativeProcessStateFromStatus,
  type NativeControlState,
} from "../../sdk-typescript/src/native-terminal/lifecycle.ts";
import type { SessionSummary } from "../../sdk-typescript/src/domain/session.ts";
import type { NativeProcessState } from "../../sdk-typescript/src/native-terminal/domain.ts";
import type { CommandReceipt } from "../../sdk-typescript/src/domain/session.ts";
import { randomUuid } from "../../sdk-typescript/src/ids.ts";

export type NativeTerminalScreenState =
  | "empty"
  | "awaiting_snapshot"
  | "visible"
  | "stale"
  | "desynchronized";

export type NativeSessionConnectionState =
  | "idle"
  | "resolving"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected"
  | "failed";

export interface NativeSessionTerminalTarget {
  sessionId: string;
  processAttemptId: string | null;
  generation: number;
}

export interface NativeSessionTerminalState {
  target: NativeSessionTerminalTarget;
  process: NativeProcessState | "unknown";
  connection: NativeSessionConnectionState;
  screen: NativeTerminalScreenState;
  control: NativeControlState;
  intent: "none" | "connect" | "start_and_connect" | "reconnect";
  recoverableError: string | null;
  operationId: string | null;
}

export interface NativeSessionTerminalConnection {
  readonly target?: { processAttemptId: string; generation: number };
  readonly initialSnapshot?: unknown;
  readonly initialSnapshotReady?: Promise<void>;
  readonly writable?: boolean;
  readonly pendingAttachment?: boolean;
  /** The owning Daemon has confirmed that this Session currently has no PTY. */
  readonly pendingAttachmentConfirmed?: boolean;
  /** The owning Daemon has acknowledged the fixed-device attach request. */
  readonly pendingAttachmentAcknowledged?: boolean;
  /** Non-recoverable observer failures must not enter the reconnect loop. */
  readonly terminalFailureReason?: string | null;
  /** Resolves when this pending stream finds a PTY or confirms there is none. */
  waitForPendingAttachResolution?(): Promise<"attached" | "waiting_for_pty">;
  onClose?(listener: () => void): () => void;
  onStreamState?(listener: (state: "connecting" | "connected" | "reconnecting" | "closed") => void): () => void;
  onDesynchronized?(listener: (message: string) => void): () => void;
  onSnapshot?(listener: (snapshot: { reset?: boolean }) => void): () => void;
  onControlState?(listener: (state: { mode: NativeControlState | "held-elsewhere" | "available" | "owned" | "unavailable" }) => void): () => void;
  requestControl?(force?: boolean): Promise<{ mode: string }>;
  close?(): void;
}

export interface NativeSessionTerminalCoordinatorOptions<Connection extends NativeSessionTerminalConnection> {
  sessionId: string;
  getSession: () => Promise<SessionSummary>;
  startSession: (operationId: string) => Promise<CommandReceipt>;
  connect: (session: SessionSummary) => Promise<Connection | null>;
  releaseConnection?: (connection: Connection) => void;
  reconnectDelayMs?: number;
  maxReconnectDelayMs?: number;
  /** Fixed-device CF streams may subscribe before a PTY attempt exists. */
  allowPendingAttach?: boolean;
}

export type NativeSessionTerminalListener = (state: NativeSessionTerminalState) => void;
export type NativeSessionTerminalConnectionListener<Connection extends NativeSessionTerminalConnection> = (connection: Connection | null) => void;

const inactiveStatuses = new Set(["stopped", "exited", "error", "archived"]);
const liveStatuses = new Set(["starting", "running", "waiting"]);

/**
 * Shared Session lifecycle coordinator. It owns lifecycle intent and target
 * fencing, while a connection adapter owns PTY bytes, observer frames and
 * lease protocol. Pages subscribe to this object; they do not decide whether
 * a stale projection means "start".
 */
export class NativeSessionTerminalCoordinator<Connection extends NativeSessionTerminalConnection> {
  private readonly options: NativeSessionTerminalCoordinatorOptions<Connection>;
  private readonly listeners = new Set<NativeSessionTerminalListener>();
  private readonly connectionListeners = new Set<NativeSessionTerminalConnectionListener<Connection>>();
  private stateValue: NativeSessionTerminalState;
  private reconcileTask: Promise<NativeSessionTerminalConnectionResult<Connection>> | null = null;
  private startTask: Promise<CommandReceipt> | null = null;
  private connection: Connection | null = null;
  private connectionCleanup: Array<() => void> = [];
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private desiredIntent: "none" | "connect" | "start_and_connect" | "reconnect" = "none";
  private operationCounter = 0;

  constructor(options: NativeSessionTerminalCoordinatorOptions<Connection>) {
    this.options = options;
    this.stateValue = {
      target: { sessionId: options.sessionId, processAttemptId: null, generation: 0 },
      process: "unknown",
      connection: "idle",
      screen: "empty",
      control: "none",
      intent: "none",
      recoverableError: null,
      operationId: null,
    };
  }

  get state(): NativeSessionTerminalState { return this.stateValue; }
  get currentConnection(): Connection | null { return this.connection; }

  subscribe(listener: NativeSessionTerminalListener): () => void {
    this.listeners.add(listener);
    listener(this.stateValue);
    return () => this.listeners.delete(listener);
  }

  /**
   * Observe the data-plane attachment independently from lifecycle state.
   * A coordinator may replace an observer after a relay gap or target fence;
   * pages must rebind their byte/snapshot listeners to that replacement
   * instead of continuing to listen to a closed connection.
   */
  onConnection(listener: NativeSessionTerminalConnectionListener<Connection>): () => void {
    this.connectionListeners.add(listener);
    listener(this.connection);
    return () => this.connectionListeners.delete(listener);
  }

  /** Attach to a live process. A stopped projection never starts implicitly. */
  connect(options: { startIfInactive?: boolean } = {}): Promise<NativeSessionTerminalConnectionResult<Connection>> {
    this.clearRetryTimer();
    this.desiredIntent = "connect";
    return this.reconcile(false, options.startIfInactive === true);
  }

  /** Attach first; start only after the authoritative read has no live attempt. */
  startAndConnect(): Promise<NativeSessionTerminalConnectionResult<Connection>> {
    this.clearRetryTimer();
    this.desiredIntent = "start_and_connect";
    return this.reconcile(false, true);
  }

  /** Re-attach the same Session/attempt without creating a new process. */
  reconnect(): Promise<NativeSessionTerminalConnectionResult<Connection>> {
    this.clearRetryTimer();
    this.desiredIntent = "reconnect";
    return this.reconcile(true, false);
  }

  /** Resume a cached attachment after a WebView/browser foreground event. */
  resume(): void {
    if (this.disposed || this.desiredIntent === "none") return;
    const current = this.connection as (Connection & { resume?: () => void }) | null;
    if (current?.resume) {
      current.resume();
      return;
    }
    void this.reconnect().catch(() => undefined);
  }

  /** Projection updates are advisory; the next reconcile still rereads authority. */
  notifyProjection(session: SessionSummary): void {
    if (this.disposed || session.id !== this.options.sessionId) return;
    this.applyProjection(session);
    if (this.desiredIntent !== "none" && liveStatuses.has(session.status) && !this.connection) {
      void this.reconcile().catch(() => undefined);
    }
  }

  markDesynchronized(message = "The terminal screen is out of sync."): void {
    if (this.disposed) return;
    this.update({ screen: "desynchronized", recoverableError: message });
  }

  clearIntent(): void {
    this.clearRetryTimer();
    this.desiredIntent = "none";
    this.update({ intent: "none" });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.clearRetryTimer();
    this.detachConnection(true);
    this.listeners.clear();
    this.connectionListeners.clear();
  }

  private async reconcile(forceReconnect = false, startIfInactive = false): Promise<NativeSessionTerminalConnectionResult<Connection>> {
    if (this.disposed) throw new Error("Native Session terminal coordinator is disposed.");
    if (this.reconcileTask) return this.reconcileTask;
    const task = this.reconcileInternal(forceReconnect, startIfInactive);
    this.reconcileTask = task;
    try {
      return await task;
    } catch (cause) {
      if (!this.disposed) {
        this.update({
          connection: "failed",
          recoverableError: cause instanceof Error ? cause.message : String(cause),
        });
      }
      throw cause;
    } finally {
      if (this.reconcileTask === task) this.reconcileTask = null;
    }
  }

  private async reconcileInternal(forceReconnect: boolean, startIfInactive: boolean): Promise<NativeSessionTerminalConnectionResult<Connection>> {
    const intent = this.desiredIntent === "none" ? "connect" : this.desiredIntent;
    this.update({ intent, connection: "resolving", recoverableError: null });
    const session = await this.options.getSession();
    if (this.disposed) throw new Error("Native Session terminal coordinator is disposed.");
    this.applyProjection(session);

    let effective = session;
    let processLive = hasLiveAttempt(session);
    // `starting`/`running`/`waiting` without an attempt is an incomplete
    // projection, not evidence that a second process should be started. Give
    // the owner a bounded window to publish its attempt first.
    if (!processLive && liveStatuses.has(session.status) && !this.options.allowPendingAttach) {
      effective = await this.waitForAttemptOrInactive();
      processLive = hasLiveAttempt(effective);
    }
    const shouldStart = !processLive
      && inactiveStatuses.has(effective.status)
      && (intent === "start_and_connect" || startIfInactive);
    if (!processLive && !shouldStart && !this.options.allowPendingAttach) {
      this.detachConnection(false);
      this.update({ connection: "disconnected", screen: "empty", control: "none", process: nativeProcessStateFromStatus(effective.status) });
      return { connection: null, started: false, session: effective, pending: false };
    }

    let started = false;
    const staleLiveProjection = intent === "connect" && liveStatuses.has(effective.status);
    const shouldProbePendingAttach = this.options.allowPendingAttach === true
      && (shouldStart || intent === "start_and_connect" || staleLiveProjection);
    if (shouldProbePendingAttach) {
      if (forceReconnect) {
        this.detachConnection(false);
        this.update({ screen: this.stateValue.screen === "visible" ? "visible" : "awaiting_snapshot", connection: "connecting", control: "none" });
      }
      if (!this.connection) {
        const connection = await this.options.connect(effective);
        if (!connection) {
          this.update({ connection: "reconnecting", screen: this.stateValue.screen === "visible" ? "visible" : "awaiting_snapshot" });
          return { connection: null, started: false, session: effective, pending: false };
        }
        if (this.disposed) {
          connection.close?.();
          throw new Error("Native Session terminal coordinator is disposed.");
        }
        this.connection = connection;
        this.notifyConnection(connection);
        this.bindConnection(connection, effective);
      }
      const pendingConnection = this.connection;
      // Preserve the connection receiver: RemoteTerminalConnection reads its
      // pending-attachment state through `this`, so passing the method itself
      // into the timeout race would invoke it detached and fail every attach.
      const waitForAttachment = pendingConnection.waitForPendingAttachResolution
        ?.bind(pendingConnection);
      if (waitForAttachment) {
        const attachResolution = await this.waitForPendingAttachResolution(waitForAttachment, pendingConnection);
        if (this.disposed || this.connection !== pendingConnection) {
          return { connection: null, started: false, session: effective, pending: false };
        }
        if (attachResolution === "attached") {
          await this.waitForInitialSnapshot(pendingConnection);
          if (this.disposed || this.connection !== pendingConnection) {
            return { connection: null, started: false, session: effective, pending: false };
          }
          this.update({
            ...(pendingConnection.target ? {
              target: {
                sessionId: this.options.sessionId,
                processAttemptId: pendingConnection.target.processAttemptId,
                generation: pendingConnection.target.generation,
              },
            } : {}),
            process: "running",
            screen: "visible",
            connection: "connected",
            recoverableError: null,
            operationId: null,
          });
          return { connection: pendingConnection, started: false, session: effective, pending: false };
        }
        // A Cloud status is only a projection. Start after the fixed-device
        // Daemon stream has confirmed no PTY; this also repairs stale-running
        // projections without attempting to start an already-live process.
        if (shouldStart || staleLiveProjection) {
          const operationId = this.stateValue.operationId ?? this.nextOperationId();
          this.update({ operationId, intent: "start_and_connect", process: "starting", connection: "connecting" });
          await this.dispatchStart(operationId);
          started = true;
          effective = hasLiveAttempt(effective)
            ? await this.waitForReplacementLiveAttempt(session)
            : await this.waitForLiveAttempt(session);
          this.applyProjection(effective);
        } else {
          this.update({
            process: nativeProcessStateFromStatus(effective.status),
            screen: this.stateValue.screen === "visible" ? "visible" : "empty",
            connection: "disconnected",
            control: "none",
            recoverableError: null,
          });
          return { connection: pendingConnection, started: false, session: effective, pending: true };
        }
      }
    }
    if (shouldStart && !started) {
      const operationId = this.stateValue.operationId ?? this.nextOperationId();
      this.update({ operationId, intent: "start_and_connect", process: "starting", connection: "connecting" });
      try {
        await this.dispatchStart(operationId);
      } catch (cause) {
        // A browser refresh can leave the previous coordinator's accepted
        // start command in flight.  A new coordinator may race it with a
        // second command; the Daemon serializes those commands and rejects
        // the latter once the first attempt is live.  Re-read authority
        // before surfacing that rejection so the new page attaches to the
        // process that actually won instead of showing a false start error.
        const refreshed = await this.options.getSession().catch(() => null);
        if (!refreshed || !hasLiveAttempt(refreshed)) throw cause;
        effective = refreshed;
        this.applyProjection(effective);
      }
      started = true;
      // The command receipt is not process readiness. Read authority until a
      // live attempt is published; the caller can keep rendering "starting".
      if (!hasLiveAttempt(effective)) effective = await this.waitForLiveAttempt(session);
    }

    const targetChanged = this.options.allowPendingAttach ? false : this.targetChanged(effective);
    if (targetChanged || forceReconnect) {
      this.detachConnection(false);
      this.update({ screen: this.stateValue.screen === "visible" ? "visible" : "awaiting_snapshot", connection: "connecting", control: "none" });
    } else if (this.connection) {
      const connection = this.connection;
      // A stopped-page attach may return immediately with its read-only
      // subscription still pending. Once the user explicitly starts, keep
      // that same subscription but do not report Start-and-connect complete
      // until the Daemon supplies the first PTY snapshot.
      if (started && connection.initialSnapshotReady) {
        await this.waitForInitialSnapshot(connection);
        if (this.disposed || this.connection !== connection) {
          return { connection: null, started, session: effective, pending: false };
        }
      }
      return { connection, started, session: effective, pending: connection.pendingAttachment === true };
    }

    let connection: Connection | null;
    try {
      connection = await this.options.connect(effective);
    } catch (cause) {
      // A Cloud projection can still advertise the previous live attempt
      // after the owning Daemon has lost that process. In that narrow case a
      // normal connect keeps targeting an attempt that can never answer a
      // snapshot. When the caller explicitly allows inactive recovery, turn
      // this one runtime-unavailable result into the same idempotent start
      // path used by Start-and-connect. Network/relay failures do not match
      // this guard and remain reconnectable without starting another Agent.
      if (!started && startIfInactive && isUnavailableProcessAttempt(cause)) {
        const operationId = this.stateValue.operationId ?? this.nextOperationId();
        this.update({ operationId, intent: "start_and_connect", process: "starting", connection: "connecting" });
        try {
          await this.dispatchStart(operationId);
        } catch (startCause) {
          const refreshed = await this.options.getSession().catch(() => null);
          if (!refreshed || !hasLiveAttempt(refreshed)) throw startCause;
          effective = refreshed;
        }
        if (!hasLiveAttempt(effective) || effective.processAttemptId === session.processAttemptId) {
          effective = await this.waitForReplacementLiveAttempt(session);
        }
        started = true;
        this.applyProjection(effective);
        if (this.targetChanged(effective)) {
          this.detachConnection(false);
          this.update({ screen: this.stateValue.screen === "visible" ? "visible" : "awaiting_snapshot", connection: "connecting", control: "none" });
        }
        connection = await this.options.connect(effective);
      } else {
        throw cause;
      }
    }
    if (!connection) {
      this.update({ connection: "reconnecting", screen: this.stateValue.screen === "visible" ? "visible" : "awaiting_snapshot" });
      return { connection: null, started, session: effective, pending: false };
    }
    if (this.disposed) {
      connection.close?.();
      throw new Error("Native Session terminal coordinator is disposed.");
    }
    this.connection = connection;
    this.notifyConnection(connection);
    this.bindConnection(connection, effective);
    this.update({
      connection: "connecting",
      screen: this.stateValue.screen === "visible" ? "visible" : "awaiting_snapshot",
      recoverableError: null,
    });
    const pendingAttachment = this.options.allowPendingAttach === true && connection.pendingAttachment === true;
    if (connection.initialSnapshotReady && (!pendingAttachment || started)) {
      if (started && this.options.allowPendingAttach) await this.waitForInitialSnapshot(connection);
      else await connection.initialSnapshotReady;
    }
    if (this.disposed || this.connection !== connection) return { connection: null, started, session: effective, pending: false };
    if (pendingAttachment) {
      this.update({ connection: "connecting", screen: "awaiting_snapshot", recoverableError: null });
    } else {
      this.update({ connection: "connected", screen: "visible", recoverableError: null, operationId: null });
    }
    return { connection, started, session: effective, pending: pendingAttachment };
  }

  private async dispatchStart(operationId: string): Promise<CommandReceipt> {
    if (this.startTask) return this.startTask;
    const task = this.options.startSession(operationId);
    this.startTask = task;
    try {
      return await task;
    } finally {
      if (this.startTask === task) this.startTask = null;
    }
  }

  private async waitForLiveAttempt(previous: SessionSummary): Promise<SessionSummary> {
    let latest = previous;
    const deadline = Date.now() + 120_000;
    while (!this.disposed && Date.now() < deadline) {
      latest = await this.options.getSession();
      this.applyProjection(latest);
      if (liveStatuses.has(latest.status) && latest.processAttemptId) return latest;
      if (inactiveStatuses.has(latest.status)) await delay(250);
      else await delay(350);
    }
    throw new Error("The Session did not publish a live process attempt.");
  }

  private async waitForReplacementLiveAttempt(previous: SessionSummary): Promise<SessionSummary> {
    let latest = previous;
    const deadline = Date.now() + 120_000;
    while (!this.disposed && Date.now() < deadline) {
      latest = await this.options.getSession();
      this.applyProjection(latest);
      if (hasLiveAttempt(latest)
        && (latest.processAttemptId !== previous.processAttemptId || latest.generation !== previous.generation)) {
        return latest;
      }
      await delay(350);
    }
    throw new Error("The Session did not publish a replacement live process attempt.");
  }

  private async waitForAttemptOrInactive(): Promise<SessionSummary> {
    let latest: SessionSummary;
    const deadline = Date.now() + 120_000;
    while (!this.disposed && Date.now() < deadline) {
      latest = await this.options.getSession();
      this.applyProjection(latest);
      if (hasLiveAttempt(latest) || inactiveStatuses.has(latest.status)) return latest;
      await delay(350);
    }
    throw new Error("The Session did not publish a live process attempt.");
  }

  private async waitForPendingAttachResolution(
    waitForAttachment: () => Promise<"attached" | "waiting_for_pty">,
    connection: NativeSessionTerminalConnection,
  ): Promise<"attached" | "waiting_for_pty"> {
    const attachment = waitForAttachment();
    const waitWithTimeout = (timeoutMs: number): Promise<"attached" | "waiting_for_pty"> => {
      let timeout: ReturnType<typeof setTimeout> | null = null;
      const deadline = new Promise<never>((_resolve, reject) => {
        timeout = setTimeout(() => reject(new Error("The owning computer did not confirm the terminal state.")), timeoutMs);
      });
      return Promise.race([attachment, deadline]).finally(() => {
        if (timeout !== null) clearTimeout(timeout);
      });
    };
    try {
      return await waitWithTimeout(30_000);
    } catch (error) {
      // `starting` is an acknowledgement from the owning Daemon, but it does
      // not settle the attach promise. OpenCode can then need additional time
      // to start its managed server and foreground PTY. Extend only this
      // acknowledged path; a silent/offline owner still fails at 30 seconds.
      if (!connection.pendingAttachmentAcknowledged) throw error;
      return await waitWithTimeout(90_000);
    }
  }

  private async waitForInitialSnapshot(connection: Connection): Promise<void> {
    if (!connection.initialSnapshotReady) return;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const deadline = new Promise<never>((_resolve, reject) => {
      timeout = setTimeout(() => reject(new Error("The terminal PTY did not provide its initial snapshot.")), 30_000);
    });
    try {
      await Promise.race([connection.initialSnapshotReady, deadline]);
    } finally {
      if (timeout !== null) clearTimeout(timeout);
    }
  }

  private bindConnection(connection: Connection, session: SessionSummary): void {
    const target = { processAttemptId: session.processAttemptId ?? "", generation: session.generation };
    if (!this.options.allowPendingAttach && connection.target && (connection.target.generation !== target.generation
      || (target.processAttemptId && connection.target.processAttemptId !== target.processAttemptId))) {
      connection.close?.();
      throw new Error("The terminal process changed while connecting.");
    }
    const cleanups: Array<() => void> = [];
    if (connection.onStreamState) cleanups.push(connection.onStreamState((state) => {
      if (this.disposed || this.connection !== connection) return;
      if (state === "connected") this.update({ connection: this.stateValue.screen === "visible" ? "connected" : "connecting" });
      else if (state === "connecting") this.update({
        connection: "connecting",
        screen: this.stateValue.screen === "visible" ? "visible" : "awaiting_snapshot",
      });
      else if (state === "reconnecting") this.update({
        connection: "reconnecting",
        screen: this.stateValue.screen === "visible"
          ? "visible"
          : this.stateValue.screen === "desynchronized"
            ? "desynchronized"
            : "awaiting_snapshot",
      });
      else if (state === "closed") this.handleConnectionLoss(connection, "The terminal connection closed.");
    }));
    if (connection.onClose) cleanups.push(connection.onClose(() => this.handleConnectionLoss(connection, "The terminal connection closed.")));
    if (connection.onSnapshot) cleanups.push(connection.onSnapshot((_snapshot) => {
      if (this.disposed || this.connection !== connection) return;
      const target = connection.target;
      this.update({
        ...(target ? {
          target: {
            sessionId: this.options.sessionId,
            processAttemptId: target.processAttemptId,
            generation: target.generation,
          },
        } : {}),
        process: "running",
        screen: "visible",
        connection: "connected",
        recoverableError: null,
        operationId: null,
      });
    }));
    if (connection.onDesynchronized) cleanups.push(connection.onDesynchronized((message) => {
      if (this.disposed || this.connection !== connection) return;
      this.markDesynchronized(message);
    }));
    if (connection.onControlState) cleanups.push(connection.onControlState((state) => {
      if (this.disposed || this.connection !== connection) return;
      this.update({ control: normalizeControlState(state.mode) });
    }));
    this.connectionCleanup = cleanups;
  }

  private handleConnectionLoss(connection: Connection, message: string): void {
    if (this.disposed || this.connection !== connection) return;
    // A closed stream is no longer a usable attachment. Clear the reference
    // before scheduling recovery so a caller re-entering the Session cannot
    // receive the already-closed connection while the retry is pending.
    this.detachConnection(false);
    if (connection.terminalFailureReason) {
      this.clearRetryTimer();
      this.desiredIntent = "none";
      this.update({
        connection: "failed",
        intent: "none",
        screen: this.stateValue.screen === "visible" ? "stale" : this.stateValue.screen,
        recoverableError: connection.terminalFailureReason,
      });
      return;
    }
    this.update({ connection: "reconnecting", screen: this.stateValue.screen === "visible" ? "visible" : "awaiting_snapshot", recoverableError: message });
    if (this.desiredIntent === "none" || this.retryTimer) return;
    const delayMs = Math.min(this.options.maxReconnectDelayMs ?? 8_000, this.options.reconnectDelayMs ?? 500);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.reconcile(true, false).catch((cause) => {
        if (!this.disposed) this.update({ connection: "reconnecting", recoverableError: cause instanceof Error ? cause.message : String(cause) });
      });
    }, delayMs);
  }

  private applyProjection(session: SessionSummary): void {
    const nextProcess = nativeProcessStateFromStatus(session.status);
    const target = { sessionId: session.id, processAttemptId: session.processAttemptId ?? null, generation: session.generation };
    const changed = this.stateValue.target.generation !== target.generation
      || this.stateValue.target.processAttemptId !== target.processAttemptId;
    this.update({
      target,
      process: nextProcess,
      screen: this.stateValue.screen,
      connection: changed && nextProcess === "running" ? "reconnecting" : this.stateValue.connection,
      control: changed ? "none" : this.stateValue.control,
    });
  }

  private targetChanged(session: SessionSummary): boolean {
    if (!this.connection) return true;
    const target = this.connection.target;
    if (!target) return false;
    return target.generation !== session.generation
      || target.processAttemptId !== (session.processAttemptId ?? target.processAttemptId);
  }

  private detachConnection(close: boolean): void {
    for (const cleanup of this.connectionCleanup.splice(0)) cleanup();
    const connection = this.connection;
    this.connection = null;
    if (connection) this.notifyConnection(null);
    if (connection) {
      if (close) connection.close?.();
      this.options.releaseConnection?.(connection);
    }
  }

  private nextOperationId(): string {
    // The counter is intentionally retained only as a readable local suffix.
    // A coordinator is recreated after a browser refresh, lock-screen resume,
    // or a route replacement; a deterministic `:1` key would then collide
    // with an older Cloud relay command and could also be mistaken for a new
    // start intent.  The UUID gives every explicit start intent a fresh
    // identity while `stateValue.operationId` keeps retries in this intent
    // single-flight and idempotent.
    this.operationCounter += 1;
    return `native-terminal-start:${this.options.sessionId}:${this.operationCounter}:${randomUuid()}`;
  }

  private update(patch: Partial<NativeSessionTerminalState>): void {
    if (this.disposed) return;
    this.stateValue = { ...this.stateValue, ...patch };
    for (const listener of this.listeners) listener(this.stateValue);
  }

  private notifyConnection(connection: Connection | null): void {
    for (const listener of this.connectionListeners) listener(connection);
  }

  private clearRetryTimer(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }
}

export interface NativeSessionTerminalConnectionResult<Connection> {
  connection: Connection | null;
  started: boolean;
  session: SessionSummary;
  pending: boolean;
}

export interface NativeSessionTerminalCoordinatorLease<Connection extends NativeSessionTerminalConnection> {
  readonly coordinator: NativeSessionTerminalCoordinator<Connection>;
  release(): void;
}

/** Runtime-owned coordinator pool. Pages acquire a lease and never own lifecycle cancellation. */
export class NativeSessionTerminalCoordinatorPool<Connection extends NativeSessionTerminalConnection> {
  private readonly entries = new Map<string, { coordinator: NativeSessionTerminalCoordinator<Connection>; leases: number; idleTimer: ReturnType<typeof setTimeout> | null }>();
  private readonly idleCloseMs: number;

  constructor(options: { idleCloseMs?: number } = {}) {
    this.idleCloseMs = options.idleCloseMs ?? 3 * 60_000;
  }

  acquire(key: string, factory: () => NativeSessionTerminalCoordinator<Connection>): NativeSessionTerminalCoordinatorLease<Connection> {
    let entry = this.entries.get(key);
    if (!entry) {
      entry = { coordinator: factory(), leases: 0, idleTimer: null };
      this.entries.set(key, entry);
    }
    entry.leases += 1;
    if (entry.idleTimer) clearTimeout(entry.idleTimer);
    entry.idleTimer = null;
    let released = false;
    return {
      coordinator: entry.coordinator,
      release: () => {
        if (released) return;
        released = true;
        entry!.leases -= 1;
        if (entry!.leases > 0) return;
        entry!.idleTimer = setTimeout(() => {
          const current = this.entries.get(key);
          if (!current || current.leases > 0) return;
          current.coordinator.dispose();
          this.entries.delete(key);
        }, this.idleCloseMs);
      },
    };
  }

  get(key: string): NativeSessionTerminalCoordinator<Connection> | null {
    return this.entries.get(key)?.coordinator ?? null;
  }

  /** Forward foreground recovery to every cached Session attachment. */
  resumeAll(): void {
    for (const entry of this.entries.values()) entry.coordinator.resume();
  }

  close(key: string): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    if (entry.idleTimer) clearTimeout(entry.idleTimer);
    entry.coordinator.dispose();
    this.entries.delete(key);
  }

  closeAll(): void {
    for (const key of this.entries.keys()) this.close(key);
  }
}

function normalizeControlState(value: string): NativeControlState {
  if (value === "owned") return "owned";
  if (value === "held-elsewhere" || value === "held_by_other") return "held_by_other";
  if (value === "acquiring") return "acquiring";
  if (value === "expired") return "expired";
  if (value === "rejected" || value === "unavailable") return "rejected";
  return "none";
}

function hasLiveAttempt(session: SessionSummary): boolean {
  return liveStatuses.has(session.status) && Boolean(session.processAttemptId);
}

function isUnavailableProcessAttempt(cause: unknown): boolean {
  const message = cause instanceof Error ? cause.message : String(cause);
  return /managed process attempt is unavailable|process attempt is unavailable|runtime unavailable/i.test(message);
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}
